import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SectionLabel } from './DashboardWrapper';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Portal,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  List,
  UnorderedList,
  ListItem,
  Button,
  Flex,
  useColorModeValue,
  Avatar,
  AvatarGroup,
  Tooltip,
  Progress,
  Input,
  InputGroup,
  InputLeftElement,
  Grid,
  Stack,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Select,
  Link,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { useUser } from '../../contexts/UserContext';
import {
  FaStar,
  FaUsers,
  FaSmile,
  FaMeh,
  FaFrown,
  FaCheck,
  FaTimes,
  FaMapMarkerAlt,
  FaPhone,
  FaGlobeAmericas,
  FaChevronDown,
  FaSearch,
  FaChartLine,
  FaComments,
  FaThumbsUp
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
  ReferenceLine,
  PieChart,
  Pie
} from 'recharts';
import RankingListComponent from '../RankingListComponent';
import ModernDetailsTable from '../ModernDetailsTable';
import BusinessReviewModal from '../BusinessReviewModal';
import BusinessBubbleChart from '../BusinessBubbleChart';
import { getYearData } from '../../services/yearService';
import { getBusinessImages } from '../../services/imageService';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';
import { getTrafficValue, sortBusinessesBySeoScore } from '../../utils/competitiveIntelHelpers';

const SentimentDashboard = ({ reviewsData, demoMode, demoData }) => {
  // Move all useColorModeValue hooks to the top level for consistency
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const searchBg = useColorModeValue('white', 'gray.700');
  const searchBorder = useColorModeValue('gray.200', 'gray.600');
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();

  const [yearData, setYearData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [businessImages, setBusinessImages] = useState({});

  // Colors for sentiment - using explicit hex values instead of theme variables
  const SENTIMENT_COLORS = {
    positive: useColorModeValue('#48BB78', '#68D391'), // green.500, green.400
    neutral: useColorModeValue('#ECC94B', '#F6E05E'), // yellow.500, yellow.400
    negative: useColorModeValue('#E53E3E', '#FC8181') // red.500, red.400
  };

  // Colors for charts - using consistent color palette with more distinct colors
  const colors = [
    '#2F80ED', // bright blue
    '#27AE60', // emerald green
    '#EB5757', // bright red
    '#9B51E0', // purple
    '#F2994A', // orange
    '#219EBC', // teal
    '#F264A6', // pink
    '#BB6BD9', // violet
    '#2D9CDB', // light blue
    '#219653' // dark green
  ];
  const [businessColors, setBusinessColors] = useState({});

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  // Custom label renderer for pie charts that shows spinning stars for favorited businesses
  const FavoriteStarPieLabel = useMemo(() => {
    return createFavoriteStarPieLabelRenderer({
      businessNameToUriMap,
      favoriteUris
    });
  }, [businessNameToUriMap, favoriteUris]);

  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setCombinedData(demoData.combinedData || []);
      setIsLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const yearDataResponse = await getYearData();
        setYearData(yearDataResponse);

        // Combine data using maps_uri as unique identifier
        const combined = reviewsData.map((currentData) => {
          const historicalData = yearDataResponse.find(
            (yd) => yd.maps_uri === currentData.maps_uri
          );

          return {
            ...currentData,
            historicalData: historicalData?.data || null
          };
        });

        setCombinedData(combined);
      } catch (error) {
        console.error('Error fetching year data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (reviewsData?.length > 0) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [reviewsData]); // Date filtering removed - sentiment analysis uses all available data

  useEffect(() => {
    // Assign colors to businesses
    const newBusinessColors = {};
    reviewsData.forEach((business, index) => {
      newBusinessColors[business.name] = colors[index % colors.length];
    });
    setBusinessColors(newBusinessColors);
  }, [reviewsData]);

  // Fetch business images for podium
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        // Don't block UI on image fetch failure
        console.error('Error fetching business images:', error);
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, []);

  const getColor = (businessName) => {
    return businessColors[businessName] || colors[0];
  };

  // Add histogram utility function
  const createHistogramBuckets = useCallback(
    (data, valueKey, bucketCount = 10) => {
      if (!data || data.length === 0) return [];

      const values = data
        .map((item) => item[valueKey])
        .filter((val) => val !== undefined && val !== null);
      if (values.length === 0) return [];

      const min = Math.min(...values);
      const max = Math.max(...values);
      const bucketSize = (max - min) / bucketCount;

      const buckets = Array.from({ length: bucketCount }, (_, i) => ({
        range: `${Math.round(min + i * bucketSize)}-${Math.round(
          min + (i + 1) * bucketSize
        )}`,
        min: min + i * bucketSize,
        max: min + (i + 1) * bucketSize,
        count: 0,
        businesses: []
      }));

      // Fill buckets
      data.forEach((item) => {
        const value = item[valueKey];
        if (value !== undefined && value !== null) {
          const bucketIndex = Math.min(
            Math.floor((value - min) / bucketSize),
            bucketCount - 1
          );
          buckets[bucketIndex].count++;
          buckets[bucketIndex].businesses.push(item);
        }
      });

      return buckets;
    },
    []
  );

  const calculateSentimentScore = (sentimentData) => {
    if (!sentimentData) return 0;
    const total =
      sentimentData.positive + sentimentData.neutral + sentimentData.negative;
    return total > 0 ? (sentimentData.positive / total) * 100 : 0;
  };

  // Calculate sentiment metrics for each business
  const businessSentimentData = useMemo(() => {
    return combinedData.map((business) => {
      const sentimentBreakdown = business.historicalData
        ?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0
      };

      const total =
        sentimentBreakdown.positive +
        sentimentBreakdown.neutral +
        sentimentBreakdown.negative;
      const sentimentScore =
        total > 0
          ? ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) /
              total) *
            100
          : 0;

      const positivePercentage =
        total > 0 ? (sentimentBreakdown.positive / total) * 100 : 0;

      return {
        ...business,
        sentimentScore,
        positivePercentage,
        ...sentimentBreakdown,
        total
      };
    });
  }, [combinedData]);

  // Filter businesses based on name and type
  const filteredBusinesses = useMemo(() => {
    if (!businessSentimentData.length) return [];
    return businessSentimentData;
  }, [businessSentimentData]);

  // Sort businesses based on sentiment score
  const sortedBySentiment = useMemo(() => {
    if (!filteredBusinesses.length) return [];

    return [...filteredBusinesses].sort((a, b) => {
      return b.sentimentScore - a.sentimentScore;
    });
  }, [filteredBusinesses]);

  // Select top businesses for display
  const top5Sentiment = useMemo(
    () => sortedBySentiment.slice(0, 5),
    [sortedBySentiment]
  );
  const bottom5Sentiment = useMemo(
    () => sortedBySentiment.slice(-5).reverse(),
    [sortedBySentiment]
  );

  const businessGrowthData = useMemo(() => {
    if (!combinedData || combinedData.length === 0) {
      return [];
    }
    return combinedData.map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      let months = Object.keys(monthlyData).sort();

      // No date filtering for sentiment analysis - use all available data

      if (months.length === 0) {
        return { ...business, threeMonthGrowth: 0 };
      }
      const lastThreeMonths = months.length >= 3 ? months.slice(-3) : months;
      const threeMonthGrowth = lastThreeMonths.reduce(
        (sum, month) => sum + monthlyData[month],
        0
      );
      return { ...business, threeMonthGrowth };
    });
  }, [combinedData]); // Date filtering removed

  // Sort businesses by growth
  const sortedByGrowth = useMemo(() => {
    if (!businessGrowthData.length) return [];
    return [...businessGrowthData].sort(
      (a, b) => b.threeMonthGrowth - a.threeMonthGrowth
    );
  }, [businessGrowthData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => {
        const trafficValue = getTrafficValue(business) || 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  const sortedBySeo = useMemo(
    () => sortBusinessesBySeoScore(combinedData),
    [combinedData]
  );

  // Sort businesses by total reviews
  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort(
      (a, b) => (b.rating_count || 0) - (a.rating_count || 0)
    );
  }, [combinedData]);

  // Calculate market-wide metrics
  const marketMetrics = useMemo(() => {
    const totalPositive = sortedBySentiment.reduce(
      (sum, b) => sum + b.positive,
      0
    );
    const totalNeutral = sortedBySentiment.reduce(
      (sum, b) => sum + b.neutral,
      0
    );
    const totalNegative = sortedBySentiment.reduce(
      (sum, b) => sum + b.negative,
      0
    );
    const totalReviews = totalPositive + totalNeutral + totalNegative;

    const marketSentimentScore =
      totalReviews > 0
        ? ((totalPositive + totalNeutral * 0.5) / totalReviews) * 100
        : 0;

    const avgSentimentScore =
      sortedBySentiment.length > 0
        ? sortedBySentiment.reduce((sum, b) => sum + b.sentimentScore, 0) /
          sortedBySentiment.length
        : 0;

    return {
      totalPositive,
      totalNeutral,
      totalNegative,
      totalReviews,
      marketSentimentScore,
      avgSentimentScore,
      positivePercentage:
        totalReviews > 0 ? (totalPositive / totalReviews) * 100 : 0,
      neutralPercentage:
        totalReviews > 0 ? (totalNeutral / totalReviews) * 100 : 0,
      negativePercentage:
        totalReviews > 0 ? (totalNegative / totalReviews) * 100 : 0
    };
  }, [sortedBySentiment]);

  // Prepare data for bar chart
  const barChartData = useMemo(() => {
    return top5Sentiment.map((business) => ({
      name: business.name.split(' ').slice(0, 2).join(' '),
      score: business.sentimentScore,
      positive: business.positive,
      neutral: business.neutral,
      negative: business.negative,
      fill: getColor(business.name)
    }));
  }, [top5Sentiment]);

  // Prepare data for pie chart
  const pieChartData = useMemo(() => {
    return [
      {
        name: 'Positive 😊',
        value: marketMetrics.totalPositive,
        fill: '#48BB78' /* Explicit green.500 */
      },
      {
        name: 'Neutral 😐',
        value: marketMetrics.totalNeutral,
        fill: '#ECC94B' /* Explicit yellow.500 */
      },
      {
        name: 'Negative 😞',
        value: marketMetrics.totalNegative,
        fill: '#E53E3E' /* Explicit red.500 */
      }
    ];
  }, [marketMetrics]);

  // Create histogram data for sentiment metrics
  const sentimentScoreHistogram = useMemo(() => {
    return createHistogramBuckets(businessSentimentData, 'sentimentScore', 16);
  }, [businessSentimentData, createHistogramBuckets]);

  const positiveReviewsHistogram = useMemo(() => {
    return createHistogramBuckets(businessSentimentData, 'positive', 16);
  }, [businessSentimentData, createHistogramBuckets]);

  // Prepare data table columns
  const columns = [
    {
      id: 1,
      name: 'Business Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '200px',
      cell: (row) => (
        <Text fontWeight='semibold' fontSize='md' noOfLines={1}>{row.name}</Text>
      )
    },
    {
      id: 2,
      name: 'Sentiment Score',
      selector: (row) => row.sentimentScore,
      sortable: true,
      right: true,
      cell: (row) => {
        const score = row.sentimentScore;
        const colorScheme = score >= 70 ? 'green' : score >= 50 ? 'yellow' : 'red';
        const icon = score >= 70 ? FaSmile : score >= 50 ? FaMeh : FaFrown;
        return (
          <HStack spacing={2} justify='flex-end'>
            <Icon as={icon} color={`${colorScheme}.400`} boxSize={4} />
            <Badge colorScheme={colorScheme} borderRadius='full' px={2} fontWeight='semibold'>
              {score.toFixed(1)}%
            </Badge>
          </HStack>
        );
      }
    },
    {
      id: 3,
      name: 'Positive',
      selector: (row) => row.positive,
      sortable: true,
      right: true,
      cell: (row) => (
        <HStack spacing={2} justify='flex-end'>
          <Text fontWeight='medium' fontFeatureSettings="'tnum' 1" color='green.600'>
            {row.positive.toLocaleString()}
          </Text>
          <Icon as={FaSmile} color='green.400' boxSize={4} />
        </HStack>
      )
    },
    {
      id: 4,
      name: 'Neutral',
      selector: (row) => row.neutral,
      sortable: true,
      right: true,
      cell: (row) => (
        <HStack spacing={2} justify='flex-end'>
          <Text fontWeight='medium' fontFeatureSettings="'tnum' 1" color='yellow.600'>
            {row.neutral.toLocaleString()}
          </Text>
          <Icon as={FaMeh} color='yellow.400' boxSize={4} />
        </HStack>
      )
    },
    {
      id: 5,
      name: 'Negative',
      selector: (row) => row.negative,
      sortable: true,
      right: true,
      cell: (row) => (
        <HStack spacing={2} justify='flex-end'>
          <Text fontWeight='medium' fontFeatureSettings="'tnum' 1" color='red.600'>
            {row.negative.toLocaleString()}
          </Text>
          <Icon as={FaFrown} color='red.400' boxSize={4} />
        </HStack>
      )
    }
  ];

  const handleBusinessClick = (business) => {
    const businessWithFullData = combinedData.find(
      (cb) => cb.maps_uri === business.maps_uri
    );
    setSelectedBusiness(businessWithFullData);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <Flex
        justify='center'
        align='center'
        minH='200px'
      >
        <Spinner
          thickness='4px'
          speed='0.65s'
          emptyColor='gray.200'
          color='blue.500'
          size='xl'
        />
      </Flex>
    );
  }

  // Business card component
  const BusinessSentimentCard = ({ business }) => {
    const sentimentIcon =
      business.sentimentScore >= 70
        ? FaSmile
        : business.sentimentScore >= 50
        ? FaMeh
        : FaFrown;

    const sentimentColor =
      business.sentimentScore >= 70
        ? SENTIMENT_COLORS.positive
        : business.sentimentScore >= 50
        ? SENTIMENT_COLORS.neutral
        : SENTIMENT_COLORS.negative;

    // Define hover background color at component level
    const hoverBg = useColorModeValue('gray.50', 'gray.700');

    return (
      <Box
        bg={cardBg}
        borderWidth='1px'
        borderColor={borderColor}
        borderRadius='lg'
        overflow='hidden'
        p={4}
        cursor='pointer'
        onClick={() => handleBusinessClick(business)}
        transition='all 0.2s'
        _hover={{
          transform: 'translateY(-4px)',
          shadow: 'lg',
          borderColor: 'blue.400',
          bg: hoverBg
        }}
        height='100%'
      >
        <VStack
          align='stretch'
          spacing={4}
          height='100%'
        >
          <Box flex='1'>
            <Heading
              size='md'
              mb={2}
              lineHeight='1.4'
              minHeight='2.0em'
              overflow='hidden'
              css={{
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical'
              }}
              title={business.name}
            >
              {business.name}
            </Heading>
            <HStack
              spacing={2}
              justify='space-between'
            >
              <Badge colorScheme='blue'>
                {business.primary_type
                  ?.replace(/_/g, ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Business'}
              </Badge>
              <HStack spacing={1}>
                <Icon
                  as={sentimentIcon}
                  color={sentimentColor}
                  boxSize={5}
                  mr={1}
                />
                <Text
                  as='span'
                  fontWeight='bold'
                >
                  {business.sentimentScore.toFixed(1)}%
                </Text>
              </HStack>
            </HStack>
          </Box>

          {/* Sentiment Distribution */}
          <Box>
            <Text
              fontSize='sm'
              fontWeight='medium'
              mb={1}
            >
              Sentiment Breakdown
            </Text>
            <SimpleGrid
              columns={3}
              spacing={2}
              mb={1}
              py={2}
            >
              <VStack>
                <Icon
                  as={FaSmile}
                  color={SENTIMENT_COLORS.positive}
                  boxSize={4}
                />
                <Text
                  as='span'
                  fontWeight='bold'
                >
                  {business.positive}
                </Text>
              </VStack>
              <VStack>
                <Icon
                  as={FaMeh}
                  color={SENTIMENT_COLORS.neutral}
                  boxSize={4}
                />
                <Text
                  as='span'
                  fontWeight='bold'
                >
                  {business.neutral}
                </Text>
              </VStack>
              <VStack>
                <Icon
                  as={FaFrown}
                  color={SENTIMENT_COLORS.negative}
                  boxSize={4}
                />
                <Text
                  as='span'
                  fontWeight='bold'
                >
                  {business.negative}
                </Text>
              </VStack>
            </SimpleGrid>

            {/* Progress bars */}
            <VStack
              spacing={1}
              align='stretch'
            >
              <Progress
                value={business.positivePercentage}
                colorScheme='green'
                size='sm'
                borderRadius='full'
                mb={1}
              />
              <Progress
                value={(business.neutral / business.total) * 100}
                colorScheme='yellow'
                size='sm'
                borderRadius='full'
                mb={1}
              />
              <Progress
                value={(business.negative / business.total) * 100}
                colorScheme='red'
                size='sm'
                borderRadius='full'
              />
            </VStack>
          </Box>

          {/* Basic Info */}
          <VStack
            align='start'
            spacing={1}
            mt='auto'
            fontSize='sm'
          >
            <HStack spacing={2}>
              <Icon
                as={FaMapMarkerAlt}
                color='gray.500'
                boxSize={4}
              />
              <Text
                as='span'
                noOfLines={1}
              >
                {business.address || 'No address available'}
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Icon
                as={FaComments}
                color='gray.500'
                boxSize={4}
              />
              <Text as='span'>{business.total} reviews</Text>
            </HStack>
          </VStack>
        </VStack>
      </Box>
    );
  };

  return (
    <Box>
      {/* Market Metrics */}
      <SimpleGrid
        columns={{ base: 1, lg: 3 }}
        spacing={6}
        mb={8}
      >
        <Box
          bg={cardBg}
          p={6}
          borderRadius='lg'
          shadow='md'
          borderWidth='1px'
          borderColor={borderColor}
        >
          <VStack
            align='start'
            spacing={4}
          >
            <HStack>
              <Icon
                as={FaSmile}
                boxSize={6}
                color={SENTIMENT_COLORS.positive}
              />
              <Heading size='md'>Overall Sentiment</Heading>
            </HStack>
            <Stat>
              <StatLabel>Market Sentiment Score</StatLabel>
              <StatNumber
                fontSize='4xl'
                color={
                  marketMetrics.marketSentimentScore >= 70
                    ? 'green.500'
                    : marketMetrics.marketSentimentScore >= 50
                    ? 'yellow.500'
                    : 'red.500'
                }
              >
                {marketMetrics.marketSentimentScore.toFixed(1)}%
              </StatNumber>
              <StatHelpText>
                Based on {marketMetrics.totalReviews.toLocaleString()} reviews
              </StatHelpText>
            </Stat>

            <Box w='100%'>
              <Text mb={2}>Sentiment Distribution</Text>
              <Box
                w='100%'
                h='16px'
                bg='gray.100'
                borderRadius='full'
                position='relative'
                overflow='hidden'
              >
                <Box
                  position='absolute'
                  left={0}
                  top={0}
                  h='100%'
                  w={`${marketMetrics.positivePercentage}%`}
                  bg={SENTIMENT_COLORS.positive}
                />
                <Box
                  position='absolute'
                  left={`${marketMetrics.positivePercentage}%`}
                  top={0}
                  h='100%'
                  w={`${marketMetrics.neutralPercentage}%`}
                  bg={SENTIMENT_COLORS.neutral}
                />
                <Box
                  position='absolute'
                  left={`${
                    marketMetrics.positivePercentage +
                    marketMetrics.neutralPercentage
                  }%`}
                  top={0}
                  h='100%'
                  w={`${marketMetrics.negativePercentage}%`}
                  bg={SENTIMENT_COLORS.negative}
                />
              </Box>
              <HStack
                mt={2}
                justify='space-between'
                fontSize='sm'
              >
                <HStack>
                  <Icon
                    as={FaSmile}
                    color={SENTIMENT_COLORS.positive}
                  />
                  <Text as='span'>
                    {marketMetrics.positivePercentage.toFixed(1)}%
                  </Text>
                </HStack>
                <HStack>
                  <Icon
                    as={FaMeh}
                    color={SENTIMENT_COLORS.neutral}
                  />
                  <Text as='span'>
                    {marketMetrics.neutralPercentage.toFixed(1)}%
                  </Text>
                </HStack>
                <HStack>
                  <Icon
                    as={FaFrown}
                    color={SENTIMENT_COLORS.negative}
                  />
                  <Text as='span'>
                    {marketMetrics.negativePercentage.toFixed(1)}%
                  </Text>
                </HStack>
              </HStack>
            </Box>
          </VStack>
        </Box>

        <Box
          bg={cardBg}
          p={6}
          borderRadius='lg'
          shadow='md'
          borderWidth='1px'
          borderColor={borderColor}
          gridColumn={{ base: 'auto', lg: 'span 2' }}
        >
          <VStack
            align='start'
            spacing={4}
            h='100%'
          >
            {/* Top 3 Podium */}
            <Flex justify='center' align='flex-end' gap={4} w='full' flex='1'>
              {/* Second Place */}
              <Box
                bg={cardBg}
                borderRadius='xl'
                textAlign='center'
                position='relative'
                cursor='pointer'
                onClick={() => handleBusinessClick(top5Sentiment[1])}
                transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                _hover={{ 
                  transform: 'translateY(-6px)', 
                  shadow: 'xl',
                }}
                overflow='hidden'
                flex='1'
                maxW='280px'
                boxShadow='lg'
                borderWidth='1px'
                borderColor={borderColor}
              >
                <Box h='3px' bgGradient='linear(to-r, gray.300, gray.500, gray.300)' />
                <VStack p={4} spacing={3}>
                  <Text fontSize='xl'>🥈</Text>
                  <Box p='2px' borderRadius='full' bgGradient='linear(to-br, gray.300, gray.500)'>
                    <Avatar
                      name={top5Sentiment[1]?.name}
                      src={businessImages[top5Sentiment[1]?.maps_uri] || ''}
                      bg='gray.100'
                      boxSize='55px'
                      borderWidth='2px'
                      borderColor={cardBg}
                    />
                  </Box>
                  <Heading size='xs' noOfLines={1} color='gray.700'>{top5Sentiment[1]?.name}</Heading>
                  <Text 
                    fontWeight='extrabold' 
                    fontSize='xl' 
                    bgGradient='linear(to-r, gray.600, gray.800)'
                    bgClip='text'
                  >
                    {top5Sentiment[1]?.sentimentScore.toFixed(1)}%
                  </Text>
                  <HStack spacing={3} fontSize='xs'>
                    <VStack spacing={0}>
                      <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} boxSize={3} />
                      <Text>{top5Sentiment[1]?.positive}</Text>
                    </VStack>
                    <VStack spacing={0}>
                      <Icon as={FaMeh} color={SENTIMENT_COLORS.neutral} boxSize={3} />
                      <Text>{top5Sentiment[1]?.neutral}</Text>
                    </VStack>
                    <VStack spacing={0}>
                      <Icon as={FaFrown} color={SENTIMENT_COLORS.negative} boxSize={3} />
                      <Text>{top5Sentiment[1]?.negative}</Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Box>

              {/* First Place */}
              <Box
                bg={cardBg}
                borderRadius='xl'
                textAlign='center'
                position='relative'
                cursor='pointer'
                onClick={() => handleBusinessClick(top5Sentiment[0])}
                transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                _hover={{ 
                  transform: 'translateY(-10px) scale(1.02)', 
                  shadow: '0 20px 40px -12px rgba(251, 191, 36, 0.4)',
                }}
                overflow='hidden'
                flex='1.15'
                maxW='320px'
                boxShadow='0 15px 30px -8px rgba(251, 191, 36, 0.3)'
                zIndex={2}
                mb={3}
                borderWidth='1px'
                borderColor='yellow.200'
              >
                <Box
                  h='4px'
                  bgGradient='linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)'
                  backgroundSize='200% 100%'
                  animation='shimmer 2s linear infinite'
                  sx={{
                    '@keyframes shimmer': {
                      '0%': { backgroundPosition: '200% 0' },
                      '100%': { backgroundPosition: '-200% 0' },
                    },
                  }}
                />
                <VStack p={5} spacing={3}>
                  <Text fontSize='2xl'>🏆</Text>
                  <Box p='3px' borderRadius='full' bgGradient='linear(to-br, yellow.300, orange.400, yellow.500)' shadow='md'>
                    <Avatar
                      name={top5Sentiment[0]?.name}
                      src={businessImages[top5Sentiment[0]?.maps_uri] || ''}
                      bg='yellow.50'
                      boxSize='70px'
                      borderWidth='3px'
                      borderColor={cardBg}
                    />
                  </Box>
                  <Heading size='sm' noOfLines={1} color='gray.800'>{top5Sentiment[0]?.name}</Heading>
                  <Text 
                    fontWeight='extrabold' 
                    fontSize='2xl' 
                    bgGradient='linear(to-r, green.500, teal.500)'
                    bgClip='text'
                  >
                    {top5Sentiment[0]?.sentimentScore.toFixed(1)}%
                  </Text>
                  <HStack spacing={4} fontSize='sm'>
                    <VStack spacing={0}>
                      <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} boxSize={4} />
                      <Text fontWeight='medium'>{top5Sentiment[0]?.positive}</Text>
                    </VStack>
                    <VStack spacing={0}>
                      <Icon as={FaMeh} color={SENTIMENT_COLORS.neutral} boxSize={4} />
                      <Text fontWeight='medium'>{top5Sentiment[0]?.neutral}</Text>
                    </VStack>
                    <VStack spacing={0}>
                      <Icon as={FaFrown} color={SENTIMENT_COLORS.negative} boxSize={4} />
                      <Text fontWeight='medium'>{top5Sentiment[0]?.negative}</Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Box>

              {/* Third Place */}
              <Box
                bg={cardBg}
                borderRadius='xl'
                textAlign='center'
                position='relative'
                cursor='pointer'
                onClick={() => handleBusinessClick(top5Sentiment[2])}
                transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                _hover={{ 
                  transform: 'translateY(-6px)', 
                  shadow: 'xl',
                }}
                overflow='hidden'
                flex='1'
                maxW='280px'
                boxShadow='lg'
                borderWidth='1px'
                borderColor={borderColor}
              >
                <Box h='3px' bgGradient='linear(to-r, orange.200, orange.400, orange.200)' />
                <VStack p={4} spacing={3}>
                  <Text fontSize='xl'>🥉</Text>
                  <Box p='2px' borderRadius='full' bgGradient='linear(to-br, orange.300, orange.500)'>
                    <Avatar
                      name={top5Sentiment[2]?.name}
                      src={businessImages[top5Sentiment[2]?.maps_uri] || ''}
                      bg='orange.50'
                      boxSize='55px'
                      borderWidth='2px'
                      borderColor={cardBg}
                    />
                  </Box>
                  <Heading size='xs' noOfLines={1} color='gray.700'>{top5Sentiment[2]?.name}</Heading>
                  <Text 
                    fontWeight='extrabold' 
                    fontSize='xl' 
                    bgGradient='linear(to-r, orange.500, orange.600)'
                    bgClip='text'
                  >
                    {top5Sentiment[2]?.sentimentScore.toFixed(1)}%
                  </Text>
                  <HStack spacing={3} fontSize='xs'>
                    <VStack spacing={0}>
                      <Icon as={FaSmile} color={SENTIMENT_COLORS.positive} boxSize={3} />
                      <Text>{top5Sentiment[2]?.positive}</Text>
                    </VStack>
                    <VStack spacing={0}>
                      <Icon as={FaMeh} color={SENTIMENT_COLORS.neutral} boxSize={3} />
                      <Text>{top5Sentiment[2]?.neutral}</Text>
                    </VStack>
                    <VStack spacing={0}>
                      <Icon as={FaFrown} color={SENTIMENT_COLORS.negative} boxSize={3} />
                      <Text>{top5Sentiment[2]?.negative}</Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Box>
            </Flex>
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Section Label */}
      {!demoMode && <SectionLabel description='Understand how customers feel about your business through AI-powered analysis of review content, ratings, and emotional tone.'>
        Customer Sentiment Analysis
      </SectionLabel>}
      {/* Main Content */}
      <Box
        bg={cardBg}
        borderRadius='lg'
        shadow='md'
        borderWidth='1px'
        borderColor={borderColor}
        mb={8}
        overflow='hidden'
      >
        <Tabs>
          <TabList
            px={4}
            pt={4}
          >
            <Tab fontWeight='medium'>Overview</Tab>
            <Tab fontWeight='medium'>Businesses</Tab>
            <Tab fontWeight='medium'>Histogram</Tab>
            <Tab fontWeight='medium'>Rankings</Tab>
            <Tab fontWeight='medium'>Details</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Panel */}
            <TabPanel>
              <SimpleGrid
                columns={{ base: 1, lg: 2 }}
                spacing={8}
              >
                {/* Sentiment Distribution Bar Chart */}
                <Box height='400px'>
                  <Heading
                    size='md'
                    mb={4}
                  >
                    Business Sentiment Comparison
                  </Heading>
                  <ResponsiveContainer
                    width='100%'
                    height='100%'
                  >
                    <BarChart
                      data={sortedBySentiment.slice(0, 20).map((business) => ({
                        name: business.name.split(' ').slice(0, 2).join(' '),
                        sentiment: business.sentimentScore,
                        positive: business.positive,
                        neutral: business.neutral,
                        negative: business.negative
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis
                        dataKey='name'
                        angle={-45}
                        textAnchor='end'
                        height={60}
                        interval={0}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <YAxis unit='%' tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }} domain={[0, (dataMax) => Math.ceil(dataMax + 10)]} />
                      <RechartsTooltip
                        formatter={(value) => `${value.toFixed(1)}%`}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box
                                bg='white'
                                p={2}
                                border='1px'
                                borderColor='gray.200'
                                borderRadius='md'
                              >
                                <Text fontWeight='bold'>
                                  {payload[0].payload.name}
                                </Text>
                                <Text>
                                  Score: {payload[0].value.toFixed(1)}% 🎯
                                </Text>
                                <Text>
                                  Positive: {payload[0].payload.positive} 😊
                                </Text>
                                <Text>
                                  Neutral: {payload[0].payload.neutral} 😐
                                </Text>
                                <Text>
                                  Negative: {payload[0].payload.negative} 😞
                                </Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey='sentiment'
                        fill='#48BB78' /* Explicitly setting a visible green color */
                        isAnimationActive={true}
                        animationDuration={1000}
                        animationBegin={0}
                        label={FavoriteStarBarLabel}
                      >
                        {sortedBySentiment.slice(0, 20).map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              index < 5
                                ? '#48BB78' /* Using explicit green.500 */
                                : '#CBD5E0'
                            }
                          />
                        ))}
                      </Bar>
                      <ReferenceLine
                        y={marketMetrics.avgSentimentScore}
                        stroke='#ED8936'
                        strokeDasharray='5 5'
                        strokeWidth={2}
                        label={{
                          value: 'Market Average',
                          position: 'right',
                          fill: '#ED8936',
                          fontSize: 12
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Market Sentiment Pie Chart */}
                <Box height='400px' overflow='visible'>
                  <Heading
                    size='md'
                    mb={4}
                  >
                    Market Sentiment Distribution
                  </Heading>
                  <ResponsiveContainer
                    width='100%'
                    height='100%'
                    style={{ overflow: 'visible' }}
                  >
                    <PieChart style={{ overflow: 'visible' }}>
                      <Pie
                        data={pieChartData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={2}
                        dataKey='value'
                        label={FavoriteStarPieLabel}
                        isAnimationActive={true}
                        animationBegin={0}
                        animationDuration={800}
                        // Explicitly set colors
                        fill='#8884d8'
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value) => value.toLocaleString()}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Businesses Panel */}
            <TabPanel>
              <Box h={{ base: "350px", md: "600px" }}>
                <BusinessBubbleChart
                  businesses={sortedBySentiment}
                  valueKey="sentimentScore"
                  valueLabel="Sentiment Score"
                  onBusinessClick={handleBusinessClick}
                  formatValue={(val) => `${val?.toFixed(1) || '0'}%`}
                  colorScheme="green"
                />
              </Box>
            </TabPanel>

            {/* Enhanced Histogram Panel */}
            <TabPanel>
              <VStack
                spacing={8}
                align='stretch'
              >
                {/* Header Section */}
                <Box
                  textAlign='center'
                  py={4}
                >
                  <Heading
                    size='lg'
                    mb={2}
                    color='green.600'
                  >
                    😊 Sentiment Distribution Analysis
                  </Heading>
                  <Text
                    fontSize='md'
                    color='gray.600'
                    maxW='600px'
                    mx='auto'
                  >
                    Explore how businesses are distributed across sentiment
                    score ranges and positive review counts to understand market
                    satisfaction patterns.
                  </Text>
                </Box>

                {/* Sentiment Score Histogram */}
                <Box
                  bg={cardBg}
                  borderRadius='xl'
                  p={6}
                  shadow='lg'
                  borderWidth='1px'
                  borderColor={borderColor}
                >
                  <HStack
                    justify='space-between'
                    align='center'
                    mb={6}
                  >
                    <VStack
                      align='start'
                      spacing={1}
                    >
                      <Heading
                        size='md'
                        color='gray.700'
                      >
                        Sentiment Score Distribution
                      </Heading>
                      <Text
                        fontSize='sm'
                        color='gray.500'
                      >
                        Distribution of {businessSentimentData.length}{' '}
                        businesses by sentiment score ranges (0-100%)
                      </Text>
                    </VStack>
                    <Badge
                      colorScheme='green'
                      fontSize='sm'
                      px={3}
                      py={1}
                    >
                      {
                        sentimentScoreHistogram.filter(
                          (bucket) => bucket.count > 0
                        ).length
                      }{' '}
                      Active Ranges
                    </Badge>
                  </HStack>

                  <Box
                    height='450px'
                    position='relative'
                  >
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <BarChart
                        data={sentimentScoreHistogram}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <defs>
                          <linearGradient
                            id='sentimentGradient'
                            x1='0'
                            y1='0'
                            x2='0'
                            y2='1'
                          >
                            <stop
                              offset='5%'
                              stopColor='#48BB78'
                              stopOpacity={0.8}
                            />
                            <stop
                              offset='95%'
                              stopColor='#48BB78'
                              stopOpacity={0.3}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray='3 3'
                          stroke='#E2E8F0'
                        />
                        <XAxis
                          dataKey='range'
                          angle={-45}
                          textAnchor='end'
                          height={80}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          label={{
                            value: 'Sentiment Score Ranges (%)',
                            position: 'insideBottom',
                            offset: -5,
                            style: { textAnchor: 'middle', fill: '#4A5568' }
                          }}
                        />
                        <YAxis
                          label={{
                            value: 'Number of Businesses',
                            angle: -90,
                            position: 'insideLeft',
                            style: { textAnchor: 'middle', fill: '#2D3748', fontWeight: 600 }
                          }}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  bg='white'
                                  p={4}
                                  boxShadow='xl'
                                  border='1px'
                                  borderColor='gray.200'
                                  borderRadius='lg'
                                  maxW='350px'
                                >
                                  <VStack
                                    align='start'
                                    spacing={2}
                                  >
                                    <Text
                                      fontWeight='bold'
                                      fontSize='md'
                                      color='green.600'
                                    >
                                      😊 Sentiment Range: {data.range}%
                                    </Text>
                                    <Text fontSize='sm'>
                                      <strong>{data.count}</strong> businesses
                                      in this range
                                    </Text>
                                    <Text
                                      fontSize='sm'
                                      color='gray.600'
                                    >
                                      {(
                                        (data.count /
                                          businessSentimentData.length) *
                                        100
                                      ).toFixed(1)}
                                      % of total market
                                    </Text>
                                    {data.businesses.length > 0 && (
                                      <>
                                        <Text
                                          fontSize='sm'
                                          fontWeight='medium'
                                          color='gray.700'
                                          mt={2}
                                        >
                                          Top Examples:
                                        </Text>
                                        {data.businesses
                                          .slice(0, 3)
                                          .map((business, idx) => (
                                            <Text
                                              key={idx}
                                              fontSize='xs'
                                              color='gray.600'
                                            >
                                              • {business.name} (
                                              {business.sentimentScore.toFixed(
                                                1
                                              )}
                                              %)
                                            </Text>
                                          ))}
                                        {data.businesses.length > 3 && (
                                          <Text
                                            fontSize='xs'
                                            color='green.500'
                                            fontStyle='italic'
                                          >
                                            ... and {data.businesses.length - 3}{' '}
                                            more businesses
                                          </Text>
                                        )}
                                      </>
                                    )}
                                  </VStack>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar
                          dataKey='count'
                          fill='url(#sentimentGradient)'
                          cursor='pointer'
                          onClick={(data) => {
                            if (
                              data &&
                              data.businesses &&
                              data.businesses.length > 0
                            ) {
                              handleBusinessClick(data.businesses[0]);
                            }
                          }}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Positive Reviews Histogram */}
                <Box
                  bg={cardBg}
                  borderRadius='xl'
                  p={6}
                  shadow='lg'
                  borderWidth='1px'
                  borderColor={borderColor}
                >
                  <HStack
                    justify='space-between'
                    align='center'
                    mb={6}
                  >
                    <VStack
                      align='start'
                      spacing={1}
                    >
                      <Heading
                        size='md'
                        color='gray.700'
                      >
                        Positive Reviews Distribution
                      </Heading>
                      <Text
                        fontSize='sm'
                        color='gray.500'
                      >
                        Distribution of businesses by number of positive reviews
                      </Text>
                    </VStack>
                    <Badge
                      colorScheme='yellow'
                      fontSize='sm'
                      px={3}
                      py={1}
                    >
                      {
                        positiveReviewsHistogram.filter(
                          (bucket) => bucket.count > 0
                        ).length
                      }{' '}
                      Active Ranges
                    </Badge>
                  </HStack>

                  <Box
                    height='450px'
                    position='relative'
                  >
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <BarChart
                        data={positiveReviewsHistogram}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <defs>
                          <linearGradient
                            id='positiveGradient'
                            x1='0'
                            y1='0'
                            x2='0'
                            y2='1'
                          >
                            <stop
                              offset='5%'
                              stopColor='#ECC94B'
                              stopOpacity={0.8}
                            />
                            <stop
                              offset='95%'
                              stopColor='#ECC94B'
                              stopOpacity={0.3}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray='3 3'
                          stroke='#E2E8F0'
                        />
                        <XAxis
                          dataKey='range'
                          angle={-45}
                          textAnchor='end'
                          height={80}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          label={{
                            value: 'Positive Review Count Ranges',
                            position: 'insideBottom',
                            offset: -5,
                            style: { textAnchor: 'middle', fill: '#4A5568' }
                          }}
                        />
                        <YAxis
                          label={{
                            value: 'Number of Businesses',
                            angle: -90,
                            position: 'insideLeft',
                            style: { textAnchor: 'middle', fill: '#2D3748', fontWeight: 600 }
                          }}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  bg='white'
                                  p={4}
                                  boxShadow='xl'
                                  border='1px'
                                  borderColor='gray.200'
                                  borderRadius='lg'
                                  maxW='350px'
                                >
                                  <VStack
                                    align='start'
                                    spacing={2}
                                  >
                                    <Text
                                      fontWeight='bold'
                                      fontSize='md'
                                      color='yellow.600'
                                    >
                                      👍 Positive Range: {data.range}
                                    </Text>
                                    <Text fontSize='sm'>
                                      <strong>{data.count}</strong> businesses
                                      in this range
                                    </Text>
                                    <Text
                                      fontSize='sm'
                                      color='gray.600'
                                    >
                                      {(
                                        (data.count /
                                          businessSentimentData.length) *
                                        100
                                      ).toFixed(1)}
                                      % of total market
                                    </Text>
                                    {data.businesses.length > 0 && (
                                      <>
                                        <Text
                                          fontSize='sm'
                                          fontWeight='medium'
                                          color='gray.700'
                                          mt={2}
                                        >
                                          Top Examples:
                                        </Text>
                                        {data.businesses
                                          .slice(0, 3)
                                          .map((business, idx) => (
                                            <Text
                                              key={idx}
                                              fontSize='xs'
                                              color='gray.600'
                                            >
                                              • {business.name} (
                                              {business.positive} positive)
                                            </Text>
                                          ))}
                                        {data.businesses.length > 3 && (
                                          <Text
                                            fontSize='xs'
                                            color='yellow.500'
                                            fontStyle='italic'
                                          >
                                            ... and {data.businesses.length - 3}{' '}
                                            more businesses
                                          </Text>
                                        )}
                                      </>
                                    )}
                                  </VStack>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar
                          dataKey='count'
                          fill='url(#positiveGradient)'
                          cursor='pointer'
                          onClick={(data) => {
                            if (
                              data &&
                              data.businesses &&
                              data.businesses.length > 0
                            ) {
                              handleBusinessClick(data.businesses[0]);
                            }
                          }}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Enhanced Statistics Grid */}
                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacing={8}
                >
                  <Box>
                    <Heading
                      size='sm'
                      mb={4}
                      color='green.600'
                    >
                      😊 Sentiment Score Statistics
                    </Heading>
                    <SimpleGrid
                      columns={{ base: 1, sm: 2 }}
                      spacing={4}
                    >
                      <Stat
                        bg='gradient-to-br from-green-50 to-green-100'
                        p={4}
                        borderRadius='xl'
                        shadow='md'
                        borderWidth='1px'
                        borderColor='green.200'
                      >
                        <StatLabel
                          fontSize='sm'
                          color='green.600'
                          fontWeight='medium'
                        >
                          🏆 Most Common Range
                        </StatLabel>
                        <StatNumber
                          fontSize='lg'
                          color='green.700'
                        >
                          {sentimentScoreHistogram.length > 0
                            ? sentimentScoreHistogram.reduce((max, bucket) =>
                                bucket.count > max.count ? bucket : max
                              ).range + '%'
                            : 'N/A'}
                        </StatNumber>
                      </Stat>

                      <Stat
                        bg='gradient-to-br from-blue-50 to-blue-100'
                        p={4}
                        borderRadius='xl'
                        shadow='md'
                        borderWidth='1px'
                        borderColor='blue.200'
                      >
                        <StatLabel
                          fontSize='sm'
                          color='blue.600'
                          fontWeight='medium'
                        >
                          📊 Active Ranges
                        </StatLabel>
                        <StatNumber
                          fontSize='lg'
                          color='blue.700'
                        >
                          {
                            sentimentScoreHistogram.filter(
                              (bucket) => bucket.count > 0
                            ).length
                          }
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  <Box>
                    <Heading
                      size='sm'
                      mb={4}
                      color='yellow.600'
                    >
                      👍 Positive Reviews Statistics
                    </Heading>
                    <SimpleGrid
                      columns={{ base: 1, sm: 2 }}
                      spacing={4}
                    >
                      <Stat
                        bg='gradient-to-br from-yellow-50 to-yellow-100'
                        p={4}
                        borderRadius='xl'
                        shadow='md'
                        borderWidth='1px'
                        borderColor='yellow.200'
                      >
                        <StatLabel
                          fontSize='sm'
                          color='yellow.600'
                          fontWeight='medium'
                        >
                          🎯 Most Common Range
                        </StatLabel>
                        <StatNumber
                          fontSize='lg'
                          color='yellow.700'
                        >
                          {positiveReviewsHistogram.length > 0
                            ? positiveReviewsHistogram.reduce((max, bucket) =>
                                bucket.count > max.count ? bucket : max
                              ).range
                            : 'N/A'}
                        </StatNumber>
                      </Stat>

                      <Stat
                        bg='gradient-to-br from-purple-50 to-purple-100'
                        p={4}
                        borderRadius='xl'
                        shadow='md'
                        borderWidth='1px'
                        borderColor='purple.200'
                      >
                        <StatLabel
                          fontSize='sm'
                          color='purple.600'
                          fontWeight='medium'
                        >
                          📈 Active Ranges
                        </StatLabel>
                        <StatNumber
                          fontSize='lg'
                          color='purple.700'
                        >
                          {
                            positiveReviewsHistogram.filter(
                              (bucket) => bucket.count > 0
                            ).length
                          }
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>
                </SimpleGrid>

                {/* Sentiment Insights Section */}
                <Box
                  bg='gradient-to-r from-green-50 to-teal-50'
                  borderRadius='xl'
                  p={6}
                  borderWidth='1px'
                  borderColor='green.200'
                >
                  <HStack
                    spacing={3}
                    mb={4}
                  >
                    <Icon
                      as={FaSmile}
                      color='green.500'
                      boxSize={5}
                    />
                    <Heading
                      size='md'
                      color='green.700'
                    >
                      Sentiment Insights
                    </Heading>
                  </HStack>
                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    spacing={4}
                  >
                    <VStack
                      align='start'
                      spacing={2}
                    >
                      <Text
                        fontSize='sm'
                        fontWeight='medium'
                        color='green.600'
                      >
                        Market Satisfaction
                      </Text>
                      <Text
                        fontSize='sm'
                        color='gray.700'
                      >
                        {sentimentScoreHistogram.length > 0 &&
                          (() => {
                            const highSentimentBuckets =
                              sentimentScoreHistogram.filter(
                                (b) =>
                                  parseFloat(b.range.split('-')[0]) >= 70 &&
                                  b.count > 0
                              );
                            const highSentimentCount =
                              highSentimentBuckets.reduce(
                                (sum, b) => sum + b.count,
                                0
                              );
                            const percentage =
                              (highSentimentCount /
                                businessSentimentData.length) *
                              100;
                            return `${percentage.toFixed(
                              1
                            )}% of businesses have high sentiment scores (70%+), indicating ${
                              percentage > 50
                                ? 'excellent'
                                : percentage > 30
                                ? 'good'
                                : 'moderate'
                            } market satisfaction`;
                          })()}
                      </Text>
                    </VStack>
                    <VStack
                      align='start'
                      spacing={2}
                    >
                      <Text
                        fontSize='sm'
                        fontWeight='medium'
                        color='green.600'
                      >
                        Review Quality
                      </Text>
                      <Text
                        fontSize='sm'
                        color='gray.700'
                      >
                        Positive review distribution shows{' '}
                        {positiveReviewsHistogram.filter((b) => b.count > 0)
                          .length > 5
                          ? 'diverse satisfaction levels across businesses'
                          : 'concentrated positive feedback patterns'}
                      </Text>
                    </VStack>
                  </SimpleGrid>
                </Box>
              </VStack>
            </TabPanel>

            {/* Rankings Panel */}
            <TabPanel>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={8}
              >
                <Box>
                  <RankingListComponent
                    title='Top Sentiment Rankings 😊'
                    data={top5Sentiment}
                    valueKey='sentimentScore'
                    unit='%'
                    side='top'
                  />
                </Box>
                <Box>
                  <RankingListComponent
                    title='Lowest Sentiment Rankings 💔'
                    data={bottom5Sentiment}
                    valueKey='sentimentScore'
                    unit='%'
                    side='bottom'
                  />
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Details Panel */}
            <TabPanel>
              <ModernDetailsTable
                data={sortedBySentiment}
                columns={columns}
                onRowClick={handleBusinessClick}
                accentColor="green"
                defaultSortColumnId={2}
                defaultSortAsc={false}
                searchPlaceholder="Search businesses..."
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Enhanced Histogram Section */}
      <Box
        bg={cardBg}
        borderRadius='xl'
        p={8}
        shadow='lg'
        borderWidth='1px'
        borderColor={borderColor}
        mb={8}
        position='relative'
        overflow='hidden'
      >
        {/* Background gradient accent */}
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          height='4px'
          bgGradient='linear(to-r, green.400, blue.400, purple.400)'
        />

        <VStack
          align='stretch'
          spacing={6}
        >
          <HStack
            justify='space-between'
            align='center'
          >
            <HStack spacing={3}>
              <Icon
                as={FaSmile}
                color='green.500'
                boxSize={6}
              />
              <VStack
                align='start'
                spacing={1}
              >
                <Heading
                  size='md'
                  color='gray.700'
                >
                  Sentiment Score Distribution
                </Heading>
                <Text
                  fontSize='sm'
                  color='gray.500'
                >
                  Distribution of {businessSentimentData.length} businesses by
                  sentiment score ranges (0-100%)
                </Text>
              </VStack>
            </HStack>
            <Badge
              colorScheme='green'
              variant='subtle'
              fontSize='sm'
              px={3}
              py={1}
              borderRadius='full'
            >
              {
                sentimentScoreHistogram.filter((bucket) => bucket.count > 0)
                  .length
              }{' '}
              Active Ranges
            </Badge>
          </HStack>

          <Box
            height='350px'
            bg={statBg}
            borderRadius='lg'
            p={4}
            position='relative'
          >
            <ResponsiveContainer
              width='100%'
              height='100%'
            >
              <BarChart
                data={sentimentScoreHistogram}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                barCategoryGap='20%'
              >
                <defs>
                  <linearGradient
                    id='sentimentGradient'
                    x1='0'
                    y1='0'
                    x2='0'
                    y2='1'
                  >
                    <stop
                      offset='5%'
                      stopColor='#48BB78'
                      stopOpacity={0.9}
                    />
                    <stop
                      offset='95%'
                      stopColor='#48BB78'
                      stopOpacity={0.6}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray='3 3'
                  stroke={borderColor}
                  opacity={0.3}
                />
                <XAxis
                  dataKey='range'
                  angle={-45}
                  textAnchor='end'
                  height={60}
                  tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                />
                <YAxis
                  tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                  domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    fontSize: '14px'
                  }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Box
                          bg={cardBg}
                          p={3}
                          boxShadow='lg'
                          border='1px'
                          borderColor={borderColor}
                          borderRadius='lg'
                          maxW='300px'
                        >
                          <VStack
                            align='start'
                            spacing={1}
                          >
                            <Text
                              fontWeight='bold'
                              fontSize='sm'
                              color='green.600'
                            >
                              😊 Range: {data.range}%
                            </Text>
                            <Text fontSize='sm'>
                              <strong>{data.count}</strong> businesses
                            </Text>
                            <Text
                              fontSize='sm'
                              color='gray.600'
                            >
                              {(
                                (data.count / businessSentimentData.length) *
                                100
                              ).toFixed(1)}
                              % of market
                            </Text>
                          </VStack>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey='count'
                  fill='url(#sentimentGradient)'
                  cursor='pointer'
                  onClick={(data) => {
                    if (data && data.businesses && data.businesses.length > 0) {
                      handleBusinessClick(data.businesses[0]);
                    }
                  }}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </VStack>
      </Box>

      {/* Add the Modal */}
      <BusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        business={selectedBusiness}
        yearData={yearData}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTotal={sortedByTotal}
        sortedByTraffic={sortedByTraffic}
        sortedBySeo={sortedBySeo}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default SentimentDashboard;
